import { AccountFeatures } from "@enums/account/AccountFeature";
import { IRunBlockRootScope } from "@interfaces/IRunBlockRootScope";
import { UserPlatform } from "@interfaces/IUser";
import { LoginService } from "@services/login.service.ajs";
import * as angular from "angular";
import { PageStateNames } from "../class/PageStateNames";
import { AccountFeatureService, FeatureAccessType } from "./accountFeature.service";
import { ConstantsService } from "./constantsService";
import { NavigationService } from "./navigation.service";
import { SessionService } from "./session.service";
import { UsersService } from "./users.service";

export class AuthorizationService {

    static $inject = ["$rootScope", "sessionService", "$q", "accountFeatureService", "constantsService", "navigationService", "loginService", "usersService"];

    constructor(
        private readonly $rootScope: IRunBlockRootScope,
        private readonly sessionService: SessionService,
        private readonly $q: angular.IQService,
        private readonly accountFeatureService: AccountFeatureService,
        private readonly constantsService: ConstantsService,
        private readonly navigationService: NavigationService,
        private readonly loginService: LoginService,
        private readonly usersService: UsersService
    ) {
    }

    authorize(): angular.IPromise<any> {
        var sendToLoginScreen = false;

        const allowAnonymous = this.$rootScope.toState?.data && this.$rootScope.toState?.data.allowAnonymous;
        const requiredLogin = !allowAnonymous;

        if (requiredLogin && !sendToLoginScreen) {

            if (this.sessionService.hasActiveSession()) {
                return this.refreshSessionIfRequired().then(() => {

                    // Special case - if we started to log into a multi log on account as a normal user, but never completed, then our current account wont be set, at this point log out and force an account switch
                    if (this.$rootScope.currentUser.platform == UserPlatform.Valkyrie && !this.$rootScope.currentUser.CurrentAccountUserMapping && this.$rootScope.currentUser.AccountUserMappings.length) {
                        this.sendToLoginScreen();
                    }

                    // If our route is foundation, but our user isnt, then go to login
                    if (this.$rootScope.toState?.data?.userPlatform == UserPlatform.Foundation && this.$rootScope.currentUser.platform != this.$rootScope.toState?.data?.userPlatform) {
                        this.sendToLoginScreen();
                    }
                });
            }
            else {
                sendToLoginScreen = true;
            }
        }
        else if (!this.$rootScope.toState?.name) {
            sendToLoginScreen = true;
        }

        if (sendToLoginScreen) {
            this.sendToLoginScreen();

            //return this.$q.reject("Unauthorized");
        }

        return this.$q.resolve();
    }

    isUserUnauthorized(): angular.IPromise<boolean> {
        return this.loginService.getAllUserAndAccountSettings().then(account => {
            const authorizedDefaultRoute = this.navigationService.getDefaultRouteForUser(account);

            if (authorizedDefaultRoute === PageStateNames.AUTHORIZATION_UNAUTHORIZED) {
                this.navigationService.goToUnauthorizedPage();

                return this.$q.resolve(true);
            }

            this.navigationService.goToStateString(authorizedDefaultRoute);
            return this.$q.reject("User is not unauthorized, redirecting from route");
        });
    }

    isUserAuthorized(roles?: string[], requiredReadFeatures?: AccountFeatures[], requiredManageFeatures?: AccountFeatures[], skipAccountFeatureCheck: boolean = false): boolean {
        if (roles?.length && !this.accountFeatureService.currentUserHasRole(roles, false)) {
            return false;
        }

        if (requiredReadFeatures?.length && !this.accountFeatureService.currentUserHasAnyFeatureAccess(requiredReadFeatures, FeatureAccessType.Read, skipAccountFeatureCheck)) {
            return false;
        }

        if (requiredManageFeatures?.length && !this.accountFeatureService.currentUserHasAnyFeatureAccess(requiredManageFeatures, FeatureAccessType.Manage, skipAccountFeatureCheck)) {
            return false;
        }

        return true;
    }

    authorizeForUser(roles?: string[], requiredReadFeatures?: AccountFeatures[], requiredManageFeatures?: AccountFeatures[], skipAccountFeatureCheck: boolean = false): angular.IPromise<any> {

        if (this.isUserAuthorized(roles, requiredReadFeatures, requiredManageFeatures, skipAccountFeatureCheck)) {
            return this.$q.resolve();
        }

        this.navigationService.goToUnauthorizedPage();

        return this.$q.reject(`AuthorizationService: User is not authorized, roles: ${roles}, requiredReadFeatures: ${requiredReadFeatures}, requiredManageFeatures: ${requiredManageFeatures}`);
    }

    authorizeForEngineer(): angular.IPromise<any> {
        if (this.usersService.isCurrentUserInternal()) {
            return this.$q.resolve();
        }

        this.navigationService.goToUnauthorizedPage();

        return this.$q.reject(`AuthorizationService: Engineer Users are not authorized, must be foundation or internal`);
    }

    authorizeFoundationOrInternal(): angular.IPromise<any> {
        if (this.usersService.isInternalOrFoundation()) {
            return this.$q.resolve();
        }

        this.navigationService.goToUnauthorizedPage();

        return this.$q.reject(`AuthorizationService: User is not authorized, must be foundation or internal`);
    }

    authorizeDevOnly(): angular.IPromise<any> {
        if (!this.constantsService.getIsLocalDevelopment() && (this.constantsService.getIsFleet() || this.constantsService.getIsProduction())) {

            this.navigationService.goToUnauthorizedPage();

            return this.$q.reject(`AuthorizationService: This page is only available in development mode`);

        }

        return this.$q.resolve();
    }

    // ---

    private sendToLoginScreen() {
        this.$rootScope.$evalAsync(() => {
            //this.$state.go(PageStateNames.AUTHENTICATION_LOGIN);
            this.sessionService.logout();
        });
    }

    private refreshSessionIfRequired(): angular.IPromise<any> {
        if (this.sessionService.hasActiveSession()) {

            return this.loginService.getAllUserAndAccountSettings();
        }
        else {
            return this
                .sessionService
                .refreshSession()
                .then(() => {
                    return this.sessionService.getAllUserAndAccountSettings();
                });
        }
    }
}
